import _ from "lodash";
import React, { useState, useRef } from "react";

// Components
import FieldSelection from "./FieldSelection";

// DS
import { Modal, Button, Tooltip } from "@toddle-edu/ds-web";

// Utils
import {
  getModalTitle,
  getDependentFields,
  isLocalContext,
  getTypeToRender,
  getFieldSelectionModalWidth,
  getFieldSelectionModalHeight,
} from "../Utils";
import { getFieldValue } from "Assessments/modules/Utils";

// third party libs.
import { connect } from "react-redux";

// Modules
import {
  updateAssignmentField,
  updateUnitPlanField,
} from "ClassRoom/components/AddAssignment/LessonPlanningAssistant/modules/LessonPlanningAssistantModule";

// Constants
import { ELEMENT_TYPE_ADD_LEARNING_GOALS } from "ClassRoom/components/AddAssignment/LessonPlanningAssistant/modules/Constants";

// Scss
import classes from "./FieldSelectionModal.scss";

const styles = {
  headerStyle: {
    padding: "20px 24px 0 32px",
  },
};

const FieldSelectionModal = props => {
  const {
    t,
    step,
    fields,
    onClose,
    fieldList,
    isUnitDependent,
    unitPlanId,
    unitPlanFields,
    unitPlanFieldList,
    unitPlanStepList,
    allFields,
    assessmentTypeLabel,
    stepList,
    assessmentDetails,
    portalType,
    updateAssignmentField,
    updateUnitPlanField,
    setContext,
  } = props;

  const [updateParams, setUpdateParams] = useState({});
  const [localContext, setLocalContext] = useState("");

  const fieldRef = useRef({});

  // call updateUnitPlan or updateAssessment based on the type of the field
  const updateInputField = params => {
    const commonParams = {
      t,
      ...params,
      isDebounce: false,
      portalType,
    };

    if (isUnitDependent) {
      updateUnitPlanField({
        ...commonParams,
        unitPlanFields,
        field_list: unitPlanFieldList,
        unitPlanId,
      });
    } else {
      updateAssignmentField({
        ...commonParams,
        assessmentDetails,
        field_list: fieldList,
      });
    }
  };

  const onClickSave = () => {
    _.forEach(fields, ({ field, config }) => {
      // context fields (like location, textTitles) are local and do not require any api call
      if (!isLocalContext({ field, config })) {
        const value = _.get(updateParams, `${field}`);

        switch (field) {
          case "unitPlan": {
            fieldRef?.current?.[field]?.setSelectedUnit?.({
              callUpdateAssessmentMutation: true,
              updateSubjectsValue: true,
              writeToCache: true,
            });

            break;
          }
          default: {
            const _fieldList = isUnitDependent ? unitPlanFieldList : fieldList;
            const params = {};

            // we are not saving values in the realtime hence
            // make a call for each subFields once user clicks save
            if (_.has(_fieldList[field], "subFields")) {
              const subFields = _.get(_fieldList[field], "subFields");
              _.forEach(subFields, subField => {
                params[`${field}_${subField.key}`] = value[subField.key];
              });
            } else {
              params[field] = value;

              const oldValue = getFieldValue({
                fields: allFields,
                uid: field,
              });

              // if value is changed then reset the dependent fields
              // for example if globalContextMYP is changed then reset explorationDevelopMYP value
              if (!_.isEqual(value, oldValue)) {
                const dependentFields = getDependentFields({
                  field,
                  fieldList: _fieldList,
                });

                if (!_.isEmpty(dependentFields)) {
                  _.forEach(dependentFields, uid => {
                    const dependentFieldValue = getFieldValue({
                      fields: allFields,
                      uid,
                    });

                    if (!_.isEmpty(dependentFieldValue)) {
                      params[uid] = _.isArray(dependentFieldValue) ? [] : null;
                    }
                  });
                }
              }
            }

            updateInputField({ params });
          }
        }
      }

      setContext(prevState => {
        return {
          ...prevState,
          [field]: localContext,
        };
      });
    });

    onClose();
  };

  const renderBody = () => {
    return _.map(fields, ({ field, config }) => {
      const type = getTypeToRender({
        field,
        fieldList,
        unitPlanFieldList,
        isUnitDependent,
      });

      return (
        <div
          key={field}
          style={{
            minWidth: "380px",
            width: getFieldSelectionModalWidth(type),
            height: getFieldSelectionModalHeight(type),
          }}
        >
          <FieldSelection
            {...props}
            field={field}
            fieldConfig={config}
            fieldRef={fieldRef}
            updateParams={updateParams}
            setUpdateParams={newValue => {
              setUpdateParams(prevValue => ({ ...prevValue, ...newValue }));
            }}
            localContext={localContext}
            setLocalContext={setLocalContext}
          />
        </div>
      );
    });
  };

  // get modal title
  const title = getModalTitle({
    t,
    field: step || _.first(fields)?.field,
    fieldList: isUnitDependent ? unitPlanFieldList : fieldList,
    stepList: isUnitDependent ? unitPlanStepList : stepList,
    unitPlanFields,
    isUnitDependent,
    assessmentTypeLabel,
    fieldConfig: _.first(fields)?.config,
  });

  // AddLearningGoals components already renders a modal so just return the body
  if (_.size(fields) === 1) {
    const firstFieldType = getTypeToRender({
      field: _.first(fields)?.field,
      fieldList,
      unitPlanFieldList,
      isUnitDependent,
    });

    if (firstFieldType === ELEMENT_TYPE_ADD_LEARNING_GOALS) return renderBody();
  }

  return (
    <Modal
      width={"fit-content"}
      height={"fit-content"}
      isOpen={true}
      onClose={onClose}
      {..._.get(_.first(fields), "config.modalStyle", {})}
    >
      <Modal.Header
        title={
          <Tooltip tooltip={title} placement={"top"}>
            <div className={classes.title}>{title}</div>
          </Tooltip>
        }
        style={styles.headerStyle}
        isClosable={true}
      />
      <Modal.Body isScrollable>{renderBody()}</Modal.Body>
      <Modal.Footer>
        <footer className={classes.footer}>
          <Button
            size={"large"}
            variant={"outlined-subtle"}
            onClick={onClose}
            testId={
              "worksheet-worksheetAssistant-PrerequisiteSelection-FieldSelectionModal-cancel"
            }
          >
            {t("common:cancel")}
          </Button>
          <Button
            size={"large"}
            variant={"progressive"}
            onClick={onClickSave}
            testId={
              "worksheet-worksheetAssistant-PrerequisiteSelection-FieldSelectionModal-save"
            }
          >
            {t("common:done")}
          </Button>
        </footer>
      </Modal.Footer>
    </Modal>
  );
};

const mapStateToProps = (state, ownProps) => {
  const {
    unitPlanDetails,
    unitPlanTemplate,
    isUnitDependent,
    assessmentDetails,
  } = ownProps;

  const { unitPlanId, unitPlanFields } = unitPlanDetails || {};
  const { allFields: assessmentFields } = assessmentDetails || {};
  const { field_list: unitPlanFieldList, step_list: unitPlanStepList } =
    unitPlanTemplate || {};

  const allFields = isUnitDependent ? unitPlanFields : assessmentFields;

  return {
    unitPlanId,
    unitPlanFields,
    unitPlanFieldList,
    unitPlanStepList,
    allFields,
  };
};

const mapActionCreators = {
  updateAssignmentField,
  updateUnitPlanField,
};

export default connect(mapStateToProps, mapActionCreators)(FieldSelectionModal);
